// Mirror of test_berrybot/app/opening_hours.py.
// When you change one, change the other and re-run
// widget/test/fixtures/opening_hours_cases.json against both.
//
// `schedule` is `{weekday_lc: {enabled, ranges: [{from, to}]}}` (strings
// "HH:MM"). `holidays` is `{YYYY-MM-DD: {name, times: [[from, to]]}}`;
// empty `times` means closed that day; non-empty overrides the weekday.
//
// `nextOpen` returns the next tz-aware Date >= `now` at which the shop
// transitions from closed to open, searching up to 14 days ahead. Returns
// null if no opening is found. A shop that is currently open returns the
// *next* opening (tomorrow's first range), not `now`.

import { toIana } from './rails-tz'

export interface DayRange { from: string; to: string }
export interface DaySchedule { enabled: boolean; ranges: DayRange[] }
export type Schedule = Partial<Record<
  'monday' | 'tuesday' | 'wednesday' | 'thursday' |
  'friday' | 'saturday' | 'sunday',
  DaySchedule
>>
export interface Holiday { name?: string; times: Array<[string, string]> }
export type Holidays = Record<string, Holiday>

const _WEEKDAYS = [
  'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday',
] as const
const _HORIZON_DAYS = 14

export function nextOpen(
  schedule: Schedule | null | undefined,
  holidays: Holidays | null | undefined,
  timeZone: string | null | undefined,
  now: Date,
): Date | null {
  if ((!schedule || !Object.keys(schedule).length) &&
      (!holidays || !Object.keys(holidays).length)) {
    return null
  }
  const tz = toIana(timeZone) || 'UTC'
  for (let offset = 0; offset < _HORIZON_DAYS; offset++) {
    const day = addDaysInZone(now, offset, tz)
    for (const [start, _end] of rangesFor(day, schedule || {}, holidays || {})) {
      const candidate = combine(day, start, tz)
      if (candidate.getTime() >= now.getTime()) return candidate
    }
  }
  return null
}

export function isOpenAt(
  when: Date,
  schedule: Schedule | null | undefined,
  holidays: Holidays | null | undefined,
  timeZone: string | null | undefined,
): boolean {
  // Default open when there's no schedule data at all — matches the
  // bot's do-no-harm rule for missing hours.
  if ((!schedule || !Object.keys(schedule).length) &&
      (!holidays || !Object.keys(holidays).length)) {
    return true
  }
  const tz = toIana(timeZone) || 'UTC'
  const day = zonedFromInstant(when, tz)
  const hm = hhmmInZone(when, tz)
  return rangesFor(day, schedule || {}, holidays || {}).some(
    ([start, end]) => start <= hm && hm <= end,
  )
}

export type OperationalStatus = 'open' | 'closed' | 'inactive'

export function computeStatus(
  operationalStatus: string | null | undefined,
  schedule: Schedule | null | undefined,
  holidays: Holidays | null | undefined,
  timeZone: string | null | undefined,
  now: Date,
): OperationalStatus {
  // Single source of truth — mirror of opening_hours.py:compute_status.
  // The live operator override (`operational_status`) takes precedence
  // for "closed"/"inactive"; "open" (or absent) defers to the schedule.
  // Schedule is still walked by nextOpen() to label the reopen time.
  const status = (operationalStatus || '').toLowerCase()
  if (status === 'inactive') return 'inactive'
  if (status === 'closed') return 'closed'
  const haveData = (schedule && Object.keys(schedule).length)
    || (holidays && Object.keys(holidays).length)
  if (!haveData) return 'open'
  return isOpenAt(now, schedule, holidays, timeZone) ? 'open' : 'closed'
}

export function formatOpen(d: Date, timeZone: string | null | undefined): string {
  const tz = toIana(timeZone) || 'UTC'
  return new Intl.DateTimeFormat('en-GB', {
    weekday: 'short', day: 'numeric', month: 'short',
    hour: '2-digit', minute: '2-digit', hour12: false, timeZone: tz,
  }).format(d).replace(',', ',')  // keep ICU output stable
}

// --- internals ---

function hhmmInZone(when: Date, tz: string): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(when)
  const get = (t: string) => parts.find((p) => p.type === t)?.value || '00'
  const h = get('hour')
  return `${h === '24' ? '00' : h}:${get('minute')}`
}

function rangesFor(day: ZonedDay, schedule: Schedule, holidays: Holidays): Array<[string, string]> {
  const iso = isoDate(day)
  const h = holidays[iso]
  if (h !== undefined) {
    // Holiday overrides the weekday — even when `times` is empty (closed).
    return (h.times || []).map((pair) => [pair[0], pair[1]] as [string, string])
  }
  const weekday = _WEEKDAYS[day.dow]
  const ds = schedule[weekday]
  if (!ds || !ds.enabled) return []
  return (ds.ranges || []).map((r) => [r.from, r.to] as [string, string])
}

interface ZonedDay { y: number; m: number; d: number; dow: number }

function zonedFromInstant(now: Date, tz: string): ZonedDay {
  // `formatToParts` returns the zoned date components.
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit', weekday: 'short',
  }).formatToParts(now)
  const get = (t: string) => parts.find((p) => p.type === t)?.value || ''
  const wd = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 } as const
  return {
    y: parseInt(get('year'), 10),
    m: parseInt(get('month'), 10),
    d: parseInt(get('day'), 10),
    dow: wd[get('weekday') as keyof typeof wd],
  }
}

function addDaysInZone(now: Date, offset: number, tz: string): ZonedDay {
  const base = zonedFromInstant(now, tz)
  // Walk by adding milliseconds at noon UTC for offset days (insensitive to DST
  // boundaries because we then re-read the zoned components).
  const utc = Date.UTC(base.y, base.m - 1, base.d + offset, 12)
  return zonedFromInstant(new Date(utc), tz)
}

function isoDate(day: ZonedDay): string {
  return `${day.y}-${String(day.m).padStart(2, '0')}-${String(day.d).padStart(2, '0')}`
}

function combine(day: ZonedDay, hhmm: string, tz: string): Date {
  const [h, m] = hhmm.split(':').map((s) => parseInt(s, 10))
  // Construct a Date by computing the offset at this zoned date+time.
  const asIfUtc = Date.UTC(day.y, day.m - 1, day.d, h, m)
  const offsetMin = zoneOffsetMinutes(new Date(asIfUtc), tz)
  return new Date(asIfUtc - offsetMin * 60_000)
}

function zoneOffsetMinutes(d: Date, tz: string): number {
  // Express the wall-clock components of `d` in the zone, then take the
  // delta between that and the same components interpreted as UTC.
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  }).formatToParts(d)
  const get = (t: string) => parseInt(parts.find((p) => p.type === t)?.value || '0', 10)
  const asUtc = Date.UTC(
    get('year'), get('month') - 1, get('day'),
    get('hour') === 24 ? 0 : get('hour'), get('minute'), get('second'),
  )
  return Math.round((asUtc - d.getTime()) / 60_000)
}
